import React, { useCallback, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Backdrop,
  CircularProgress,
  Typography,
} from '@mui/material';
import { TECHNICAL_POLICY_COLUMNS } from '../../../../services/technicalPolicy/technicalPolicyColumns';
import { ErrorBanner } from '../../../../ui';
import EditableTable from '../../components/EditableTable';
import { useNiveauDePriorisationTab } from '../tabs/hooks/NiveauDePriorisationHook';

const labels = {
  niveauDePriorisation: 'Niveaux de priorisation',
  sauvegarder: 'Sauvegarder',
  annuler: 'Annuler',
  fermer: 'Fermer',
  suivant: 'Suivant',
};

export const NiveauPriorisationDialog = (props) => {
  const { t } = useTranslation();
  const { editMode, open, setOpen, saveData } = props;
  const [saveSchedulingLevelError, setSaveSchedulingLevelError] = useState(null);
  const { sortedNiveauDePriorisation, status } = useNiveauDePriorisationTab();
  const methods = useForm({ defaultValues: {}, mode: 'onChange' });
  const { dirtyFields } = methods.formState;
  const reset = methods.reset;
  const isDirty = Object.keys(dirtyFields ?? {}).length !== 0;

  const save = useCallback(() => {
    const values = methods.getValues();
    const reset = methods.reset;
    saveData(values, reset, setSaveSchedulingLevelError);
  }, [methods, saveData, setSaveSchedulingLevelError]);

  const handleDiscardChanges = useCallback(() => {
    reset({});
    setOpen(false);
  }, [reset, setOpen]);

  const clearSaveSchedulingLevelError = () => {
    setSaveSchedulingLevelError(null);
  };

  const currentRows = methods.watch('NiveauDePriorisation') || sortedNiveauDePriorisation || [];
  const columns = TECHNICAL_POLICY_COLUMNS.NiveauDePriorisation(
    // isIdentifierUnique, isPrioritizationRankUnique
    (row) => currentRows.some((r) => r.Identifiant === row.Identifiant && r.id !== row.id),
    (row) => currentRows.some((r) => r.RangPriorisation === row.RangPriorisation && r.id !== row.id)
  );

  const confirmDeleteDialogLabels = {
    id: 'delete-scheduling-level',
    labels: {
      title: 'Supprimer le niveau de priorisation ?',
      body:
        "Le niveau de priorisation pourrait être utilisé dans une règle de gestion ou une sequence d'actions " +
        'imposées',
      button1: 'Annuler',
      button2: 'Supprimer',
    },
  };

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <ErrorBanner
        error={saveSchedulingLevelError}
        clearErrors={clearSaveSchedulingLevelError}
        labels={{
          dismissButtonText: t('commoncomponents.banner.button.dismiss', 'Dismiss'),
          tooLongErrorMessage: t(
            'commoncomponents.banner.tooLongErrorMessage',
            'Detailed error message is too long to be displayed. ' +
              'To read it, please use the COPY button and paste it in your favorite text editor.'
          ),
          secondButtonText: t('commoncomponents.banner.button.copy.label', 'Copy'),
          toggledButtonText: t('commoncomponents.banner.button.copy.copied', 'Copied'),
        }}
      />
      <DialogTitle>{labels.niveauDePriorisation}</DialogTitle>
      <DialogContent>
        <Box width={'100%'}>
          <FormProvider {...methods} key={`form-technical-policy-wizard-niveau-priorisation`}>
            <Controller
              name="NiveauDePriorisation"
              defaultValue={sortedNiveauDePriorisation ?? []}
              render={({ field }) => {
                const { value, onChange } = field;
                return (
                  <EditableTable
                    id="NiveauDePriorisation"
                    columns={columns.filter((col) => col.isInvisible !== true)}
                    rows={value ?? []}
                    onChange={onChange}
                    editMode={editMode}
                    confirmDeleteRow={true}
                    confirmDeleteRowDialogProps={confirmDeleteDialogLabels}
                  />
                );
              }}
            />
          </FormProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ width: 1, display: 'flex', justifyContent: 'space-between', gap: 2, m: 2 }}>
          <Button
            data-cy="close-niveau-priorisation-dialog-button"
            onClick={handleDiscardChanges}
            variant="outlined"
            color="secondary"
          >
            {isDirty ? labels.annuler : labels.fermer}
          </Button>
          {editMode && (
            <Button
              data-cy="save-technical-priorisation-button"
              disabled={!isDirty}
              onClick={save}
              variant="contained"
              color="primary"
            >
              {labels.sauvegarder}
            </Button>
          )}
        </Box>
      </DialogActions>
      <Backdrop open={status === 'SAVING'}>
        <CircularProgress data-cy="saving-niveau-priorisation-spinner" color="inherit" />
        <Typography variant="h4">{labels.sauvegardeEnCours}</Typography>
      </Backdrop>
    </Dialog>
  );
};

NiveauPriorisationDialog.propTypes = {
  editMode: PropTypes.bool,
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
  saveData: PropTypes.func.isRequired,
};
